// src/theme.ts
import { createTheme } from '@mui/material/styles';

// Import Noto Sans Japanese font
import '@fontsource/noto-sans-jp/300.css';
import '@fontsource/noto-sans-jp/400.css';
import '@fontsource/noto-sans-jp/500.css';
import '@fontsource/noto-sans-jp/700.css';

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}
const theme = createTheme({
  palette: {
    primary: {
      main: '#0047BA', // BA blue
      light: '#3A6BB3',
      dark: '#003A7A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#717171', // grayscale
      light: '#9A9A9A',
      dark: '#4A4A4A',
      contrastText: '#FFFFFF',
    },
    tertiary: {
      main: '#B00020', // red
      light: '#E03A5A',
      dark: '#B00625',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#B00020', // red
      light: '#E03A5A',
      dark: '#B00625',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FF9800',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#0047BA', // BA blue
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#4CAF50',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F9F9FF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1C20', // BA black
      secondary: '#717171', // grayscale
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },

  // 2. Typography
  typography: {
    fontFamily: '"Noto Sans JP"',

    // h1 <-- Display Large
    h1: {
      fontWeight: 400, // Normal
      fontSize: '3.5625rem', // 57px
      lineHeight: 72 / 57, // 1.26
      letterSpacing: `${-0.25 / 16}rem`,
    },
    // h2 <-- Display Medium
    h2: {
      fontWeight: 400, // Normal
      fontSize: '2.8125rem', // 45px
      lineHeight: 56 / 45, // 1.24
      letterSpacing: '0rem',
    },
    // h3 <-- Display Small
    h3: {
      fontWeight: 400, // Normal
      fontSize: '2.25rem', // 36px
      lineHeight: 48 / 36, // 1.33
      letterSpacing: '0rem',
    },
    // h4 <-- Headline Large
    h4: {
      fontWeight: 400, // Normal
      fontSize: '2rem', // 32px
      lineHeight: 40 / 32, // 1.25
      letterSpacing: '0rem',
    },
    // h5 <-- Headline Medium
    h5: {
      fontWeight: 400, // Normal
      fontSize: '1.75rem', // 28px
      lineHeight: 36 / 28, // 1.28
      letterSpacing: '0rem',
    },
    // h6 <-- Headline Small
    h6: {
      fontWeight: 400, // Normal
      fontSize: '1.5rem', // 24px (common value for h6)
      lineHeight: 32 / 24, // 1.33
      letterSpacing: '0rem',
    },
    // subtitle1 <-- Title Large
    subtitle1: {
      fontWeight: 700, // Bold
      fontSize: '1rem', // 16px
      lineHeight: 28 / 16, // 1.75
      letterSpacing: `${0.15 / 16}rem`,
    },
    // subtitle2 <-- Title Small
    subtitle2: {
      fontWeight: 400, // Normal
      fontSize: '0.875rem', // 14px
      lineHeight: 20 / 14, // 1.43
      letterSpacing: `${0.1 / 16}rem`,
    },
    // body1 <-- Body Large
    body1: {
      fontWeight: 400, // Normal
      fontSize: '1rem', // 16px
      lineHeight: 1.5, // --- (reasonable default value)
      letterSpacing: `${0.15 / 16}rem`,
    },
    // body2 <-- Body Medium
    body2: {
      fontWeight: 400, // Normal
      fontSize: '0.875rem', // 14px
      lineHeight: 20 / 14, // 1.43
      letterSpacing: `${0.25 / 16}rem`,
    },
    // button <-- Label Medium
    // button: {
    //   fontWeight: 700, // Bold
    //   fontSize: '0.75rem', // 12px
    //   lineHeight: 16 / 12, // 1.33
    //   letterSpacing: `${0.2 / 16}rem`,
    //   textTransform: 'none', // Keep original configuration
    // },
    // caption <-- Body Small
    caption: {
      fontWeight: 400, // Normal
      fontSize: '0.75rem', // 12px
      lineHeight: 16 / 12, // 1.33
      letterSpacing: `${0.4 / 16}rem`,
    },
    // overline <-- Label Small
    overline: {
      fontWeight: 700, // Bold
      fontSize: '0.6875rem', // 11px
      lineHeight: 16 / 11, // 1.45
      letterSpacing: `${0.5 / 16}rem`,
      textTransform: 'none', // Override default 'uppercase' for overline
    },
  },

  // 3. Shape
  shape: {
    borderRadius: 8,
  },

  // 4. Override the Component's default Style
  components: {
    // Customize Button
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          textTransform: 'none', // Don't capitalize text
          fontWeight: 500,
          borderRadius: 8,
          ...(ownerState.variant === 'contained' && {
            boxShadow: 'none', // Remove shadow for contained button
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0, 78, 162, 0.3)',
            },
            backgroundColor: '#0047BA',
          }),
          ...(ownerState.variant === 'outlined' && {
            borderWidth: 1,
            '&:hover': {
              borderWidth: 1,
            },
          }),
        }),
      },
    },
    // Customize Card
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
          borderRadius: 8,
        },
      },
    },
    // Customize TextField
    MuiTextField: {
      defaultProps: {
        variant: 'outlined', // Default to outlined variant
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0047BA',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#0047BA',
          },
          '& .MuiInputLabel-asterisk': {
            color: '#B00020',
          },
        },
      },
    },
    // Customize AppBar
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    // Customize FormControl (for required asterisk)
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-asterisk': {
            color: '#B00020',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#0047BA',
          },
        },
      },
    },
    // Customize Select
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0047BA',
          },
        },
      },
    },
    // Customize MenuItem (for select options)
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#D7E2FF',
            color: '#0D2F60',
            '&:hover': {
              backgroundColor: '#D7E2FF',
            },
          },
        },
      },
    },
    // Customize Autocomplete (for autocomplete options)
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: '2px solid rgba(0, 0, 0, 0.23)',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          marginTop: '4px',
        },
        option: {
          '&.Mui-focused': {
            backgroundColor: '#D7E2FF',
            color: '#0D2F60',
          },
          '&[aria-selected="true"]': {
            backgroundColor: '#D7E2FF',
            color: '#0D2F60',
            '&.Mui-focused': {
              backgroundColor: '#D7E2FF',
            },
          },
        },
      },
    },
    // Customize Typography
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"Noto Sans JP", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
  },
});

export default theme;
